/**
 * Calendar days and wall-clock times in a named timezone, for things that happen at a set hour
 * where the project is: a snooze ending or a repeating task coming back.
 */

export interface CalendarDay {
  year: number;
  /** 1–12. */
  month: number;
  day: number;
}

/** The date at `now` in `timeZone`. */
export function dayInZone(now: Date, timeZone: string): CalendarDay {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(now);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { year: part('year'), month: part('month'), day: part('day') };
}

export function addDays(date: CalendarDay, days: number): CalendarDay {
  const d = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/** Whole days from `a` to `b` (negative when `b` is earlier). */
export function daysBetween(a: CalendarDay, b: CalendarDay): number {
  return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86_400_000);
}

/** 0 = Sunday … 6 = Saturday. */
export function weekday(date: CalendarDay): number {
  return new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay();
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** "2026-10-05" → a CalendarDay, or null when it isn't a real date. */
export function parseCalendarDay(value: unknown): CalendarDay | null {
  const match = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

export function formatCalendarDay(date: CalendarDay): string {
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
}

/** Today's date (YYYY-MM-DD) in `timeZone`, or the UTC date when Intl doesn't know the zone. */
export function ymdInZone(now: Date, timeZone: string): string {
  try {
    return formatCalendarDay(dayInZone(now, timeZone));
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** How far `timeZone` is ahead of UTC at `at`, in ms. */
function zoneOffset(at: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(new Date(at));
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'));
  return asUtc - Math.floor(at / 1000) * 1000;
}

/** Minutes since midnight on the wall clock in `timeZone` at `at` (0–1439). */
export function minuteOfDayInZone(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', hour: 'numeric', minute: 'numeric' }).formatToParts(at);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return (part('hour') % 24) * 60 + part('minute');
}

/** The moment it's `hour`:00 on `date` in `timeZone` (checked twice, for a clock change that day). */
export function wallClockTime(date: CalendarDay, hour: number, timeZone: string): Date {
  const guess = Date.UTC(date.year, date.month - 1, date.day, hour);
  let at = guess - zoneOffset(guess, timeZone);
  at = guess - zoneOffset(at, timeZone);
  return new Date(at);
}
